import { Pressable, Text, View } from "react-native";
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import SolidButton from "@/components/General/SolidButton";
import SelectInput from "@/components/General/inputs/SelectInput";
import { useCallback, useEffect, useMemo, useState } from "react";
import Toast from "react-native-toast-message";
import { formatSize } from "@/utils/FileManager";
import Papa from 'papaparse';
import { ActivityIndicator } from "react-native-paper";
import { Query } from "@/utils/react-query/Queries";
import { QueryKeys } from "@/utils/react-query/QueryKeys";
import stringSimilarity from "@/utils/stringSimilarity";
import usePlatform from "@/utils/hooks/usePlatform";
import { useLocalSearchParams, useNavigation, useRouter } from "expo-router";
import EmptyListPlaceholder from "@/components/General/EmptyListPlaceholder";
import { Table } from "@/components/General/Table";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { importFields, personImportFieldGroups } from "@/utils/constants/importFields"
import Unicon from "@/components/General/Unicon";
import {Tooltip} from "@/components/General/Tooltip";
import SheetSelector from "@/components/General/inputs/SheetSelector";
import { useMutation } from "@tanstack/react-query";
import { importTransactions } from "@/data/contributions";
import { importPeople } from "@/data/people";
import Checkbox from "@/components/General/inputs/Checkbox";
import DropdownMenu from "@/components/General/DropdownMenu";
import SheetModal from "@/components/General/SheetModal";
import Input from "@/components/General/inputs/Input";
import { createImportTemplate, getCurrentOrgID } from "@/data/organization";
import { queryClient, supabase } from "@/data/client";
import PageTitle from "@/components/General/PageTitle";
import useOrganizationEntitlements from "@/utils/hooks/useOrganizationEntitlements";
import { isFreeOrg } from "@/utils/organizationEntitlements";

// Custom-field types that make sense to create directly from a CSV column during import.
const NEW_CUSTOM_FIELD_TYPES = ["Text", "Long Text", "Number", "Date", "Checkbox"];

const normalizeImportAssociationTable = (table) => String(table ?? '').trim().toLowerCase();
const normalizeImportAssociationValue = (value) => String(value ?? '').trim().toLowerCase();
const importAssociationKey = (table, value) => `${normalizeImportAssociationTable(table)}::${normalizeImportAssociationValue(value)}`;

export default function ImportDataScreen() {
    const [file, setFile] = useState(null)
    const [loadingCSV, setLoadingCSV] = useState(false)
    const [csvData, setCsvData] = useState([])
    const [headers, setHeaders] = useState({})
    const [mappedColumns, setMappedColumns] = useState({})
    const [showAssociations, setShowAssociations] = useState(false)
    const [currentAssociations, setCurrentAssociations] = useState([])
    const [allPeople, setAllPeople] = useState([])
    const [associationFilter, setAssociationFilter] = useState('all')

    const [newTemplateName, setNewTemplateName] = useState("")
    const [createTemplateModalOpen, setCreateTemplateModalOpen] = useState(false)

    // For the people import: CSV columns the user wants imported as brand-new custom fields.
    // Keyed by CSV header -> { name, type }.
    const [newCustomFields, setNewCustomFields] = useState({})

    // Fields that were auto-mapped (vs user-confirmed). Keyed by field name.
    // Auto-mapped fields show a yellow checkmark; user-confirmed show green.
    const [autoMappedFields, setAutoMappedFields] = useState({})

    // Confirmation modal state for people import.
    const [confirmImportOpen, setConfirmImportOpen] = useState(false)
    const [existingRecordMode, setExistingRecordMode] = useState('skip')

    // Breakdown modal: clicking a stat card opens a table showing the specific rows.
    const [breakdownView, setBreakdownView] = useState(null) // 'new' | 'existing' | null

    // All people ImportAssociations values for this org, fetched directly from
    // the table (not the RPC, which INNER JOINs to People and drops deleted-person rows).
    const [peopleAssociationValues, setPeopleAssociationValues] = useState(null)

    const {isWeb} = usePlatform();
    const {theme: {colors, fonts}} = useUnistyles();
    const navigation = useNavigation();
    const router = useRouter();
    const params = useLocalSearchParams();
    const type = params.type;

    // Custom-field mapping is paid-only — Free imports are scalar fields only
    // (importPeople skips custom patches/field creation server-side too).
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);
    const {data: customFields = []} = Query.personCustomFields.all({ options: { enabled: !isFree } })
    const {data: importAssociations, isLoading: importAssociationsLoading} = Query.importAssociations.all()
    const {data: importTemplates = []} = Query.importTemplates.all()

    const associationLookup = useMemo(() => {
        const confirmed = new Map();
        const suggested = new Map();

        currentAssociations.forEach((association) => {
            const key = importAssociationKey(association.associatedTable, association.value);
            if (association.suggested === true) {
                if (!suggested.has(key)) suggested.set(key, association);
                return;
            }
            confirmed.set(key, association);
        });

        return {confirmed, suggested};
    }, [currentAssociations]);

    const getImportAssociationForValue = useCallback((table, value) => {
        const key = importAssociationKey(table, value);
        return associationLookup.confirmed.get(key) || associationLookup.suggested.get(key) || null;
    }, [associationLookup]);

    const createTemplateMutation = useMutation({
        mutationFn: createImportTemplate,
        onSuccess: () => {
            Toast.delayShow({type: 'success',text2: "Template created successfully!"})
            queryClient.invalidateQueries({queryKey: QueryKeys.importTemplates.all()})
            setCreateTemplateModalOpen(false)
            setNewTemplateName("")
        },
        onError: (error) => {
            Toast.show({type: 'error',text2: error.message})
        }
    })

    const importDataMutation = useMutation({
        mutationFn: (options) => {
            switch(type){
                case "transactions":
                    return importTransactions(options)
                case "people":
                    return importPeople(options)
                default:
                    Toast.show({type: 'error',text2: "Invalid import type"})
                    return Promise.resolve()
            }
        },
        onSuccess: () => {
            Toast.delayShow({type: 'success',text2: "Import successful!"})
            router.canGoBack() ? router.back() : router.replace("/")
        },
        onError: (error) => {
            Toast.show({type: 'error',text2: error.message})
        }
    })

    useEffect(() => {
        if(file){
            loadFileContents(file.uri)
        }
    }, [file])

    useEffect(() => {
        if(importAssociationsLoading) return;
        setCurrentAssociations(importAssociations || [])
    }, [importAssociationsLoading, importAssociations])

    // Fetch all people ImportAssociations values directly from the table when
    // a people import has the Source ID column mapped. We can't use the
    // get_import_associations RPC here because it INNER JOINs to People and
    // drops associations for deleted people — which would undercount matches.
    // Paginate because Supabase caps queries at 1000 rows by default.
    useEffect(() => {
        if(type !== "people") return;
        const idHeader = mappedColumns[personImportFieldGroups.idField];
        if(!idHeader || !csvData.length){
            setPeopleAssociationValues(null);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const orgID = await getCurrentOrgID();
                const pageSize = 1000;
                let allValues = [];
                let offset = 0;

                while(true){
                    const {data, error} = await supabase
                        .from("ImportAssociations")
                        .select("value")
                        .eq("organizationID", orgID)
                        .eq("associatedTable", "people")
                        .range(offset, offset + pageSize - 1);

                    if(error || !data) break;
                    allValues.push(...data.map((a) => String(a.value).trim()));
                    if(data.length < pageSize) break;
                    offset += pageSize;
                }

                if(!cancelled){
                    setPeopleAssociationValues(new Set(allValues));
                }
            } catch {
                if(!cancelled) setPeopleAssociationValues(null);
            }
        })();

        return () => { cancelled = true; };
    }, [type, mappedColumns, csvData]);
    

    useEffect(() => {
        navigation.setOptions({
            headerTitle: () => <PageTitle title={`Import ${type[0].toUpperCase() + type.slice(1)}`} />
        });
    }, [navigation, type])

    useEffect(() => {
        if (!showAssociations) {
            setAllPeople([]);
            return;
        }

        const fetchAllPeople = async () => {
            try {
                const orgID = await getCurrentOrgID();
                const { data, error } = await supabase.rpc("filter_people", {
                    filters: { search: undefined, includeArchived: 'true' },
                    organizationid: orgID,
                    logictype: 'AND',
                    record_limit: 100000,
                    record_offset: 0,
                    return_fields: ['id', 'firstName', 'lastName', 'email']
                });

                if (error) throw error;

                const people = data?.[0]?.people?.results?.map(p => ({
                    ...p,
                    HouseholdMembers: p.Address?.People?.filter(a => a.id !== p.id).map(a => a)
                })) ?? [];

                setAllPeople(people);
            } catch (err) {
                console.error('Failed to fetch people for import matching:', err);
            }
        };

        fetchAllPeople();
    }, [showAssociations]);

    useEffect(() => {
        if (!showAssociations || !allPeople.length || !csvData.length || !Object.keys(headers).length) return;

        const isNameColumn = (header) => {
            const lower = header.toLowerCase().replace(/[_\s-]/g, '');
            const excluded = ['filename', 'username', 'domainname', 'companyname', 'productname', 'fundname', 'methodname', 'batchname'];
            if (excluded.some(ex => lower.includes(ex))) return false;
            return ['name', 'firstname', 'lastname', 'fname', 'lname', 'payeename', 'personname', 'givername', 'givername', 'payee', 'giver', 'giver'].some(p => lower.includes(p));
        };

        const findPersonByName = (searchName, people) => {
            const normalized = searchName.toLowerCase().trim().replace(/\s+/g, ' ');
            if (!normalized) return null;

            let match = people.find(p => {
                const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim().toLowerCase();
                return fullName === normalized;
            });
            if (match) return match;

            match = people.find(p => {
                const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim().toLowerCase();
                return fullName.includes(normalized) || normalized.includes(fullName);
            });
            if (match) return match;

            return null;
        };

        const newSuggestions = [];
        const newSuggestionKeys = new Set();

        Object.keys(headers).forEach(header => {
            const mappedToColumn = Object.keys(mappedColumns).find(key => mappedColumns[key] === header);
            const associationField = importFields[type].filter(f => f.type)?.find(f => f.name === mappedToColumn);

            if (!associationField || associationField.type !== 'people') return;

            const nameColumnHeaders = Object.keys(headers).filter(h => isNameColumn(h));
            const uniqueValues = [...new Set(csvData.map(row => row[header]))];

            uniqueValues.forEach(value => {
                const associationKey = importAssociationKey(associationField.type, value);
                if (!normalizeImportAssociationValue(value)) return;
                if (associationLookup.confirmed.has(associationKey) || newSuggestionKeys.has(associationKey)) return;

                const representativeRow = csvData.find(row => row[header] === value);
                if (!representativeRow) return;

                const searchName = nameColumnHeaders
                    .map(h => representativeRow[h])
                    .filter(Boolean)
                    .join(' ')
                    .trim();

                if (!searchName) return;

                const matchedPerson = findPersonByName(searchName, allPeople);
                if (matchedPerson) {
                    newSuggestions.push({
                        associatedItem: matchedPerson,
                        associatedID: matchedPerson.id,
                        associatedTable: associationField.type,
                        value,
                        suggested: true
                    });
                    newSuggestionKeys.add(associationKey);
                }
            });
        });

        if (newSuggestions.length > 0) {
            setCurrentAssociations(prev => {
                const existingValues = new Set(prev.map(a => importAssociationKey(a.associatedTable, a.value)));
                const deduped = newSuggestions.filter(s => !existingValues.has(importAssociationKey(s.associatedTable, s.value)));
                if (deduped.length === 0) return prev;
                return [...prev, ...deduped];
            });
        }
    }, [showAssociations, allPeople, csvData, mappedColumns, headers, type, associationLookup]);

    const loadFileContents = async (fileUri) => {
        const fileContent = isWeb ? await fetch(fileUri).then(res => res.text()) : await FileSystem.readAsStringAsync(fileUri);
       
        Papa.parse(fileContent, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const rows = results.data;
                const csvHeaders = (results.meta.fields || []).filter(h => h && h.trim() !== '' && !h.startsWith('_'));

                // Build the list of mappable Reach fields: standard fields + custom fields.
                const standardFields = importFields[type].map(f => ({name: f.name, label: f.label}));
                const customFieldEntries = (type === 'people' ? customFields : []).map(cf => ({name: cf.id, label: cf.name}));
                const allFields = [...standardFields, ...customFieldEntries];

                const normalize = (s) => s.toLowerCase().replace(/[_\s-]/g, '');

                // Score every (header, field) pair by similarity of normalized name AND label.
                const candidates = [];
                for(const header of csvHeaders){
                    const normHeader = normalize(header);
                    for(const field of allFields){
                        const simName = stringSimilarity(normHeader, normalize(field.name));
                        const simLabel = stringSimilarity(normHeader, normalize(field.label));
                        const similarity = Math.max(simName, simLabel);
                        if(similarity > 0.8){
                            candidates.push({header, fieldName: field.name, similarity});
                        }
                    }
                }

                // Greedy one-to-one assignment: best similarity first, no header or field reused.
                candidates.sort((a, b) => b.similarity - a.similarity);
                const usedHeaders = new Set();
                const usedFields = new Set();
                const autoMapped = {};
                const autoMappedFlags = {};

                for(const {header, fieldName} of candidates){
                    if(usedHeaders.has(header) || usedFields.has(fieldName)) continue;
                    usedHeaders.add(header);
                    usedFields.add(fieldName);
                    autoMapped[fieldName] = header;
                    autoMappedFlags[fieldName] = true;
                }

                const processedHeaders = csvHeaders.reduce((acc, header) => {
                    acc[header] = "";
                    return acc;
                }, {});

                setHeaders(processedHeaders);
                setCsvData(rows);
                setMappedColumns(autoMapped);
                setAutoMappedFields(autoMappedFlags);
                setLoadingCSV(false);
              },
              error: (error) => {
                Toast.show({
                    type: 'error',
                    text2: "Could not parse CSV file: " + error.message
                })
              },
        });
    }

    const sampleValuesFor = (header) => {
        if(!header) return [];
        const first = csvData.find((row) => row[header] !== "" && row[header] != null)?.[header];
        const second = csvData.find((row) => row[header] !== "" && row[header] != null && row[header] !== first)?.[header];
        return [first, second].filter(Boolean);
    };

    const mappingColumns = useMemo(() => [
        {
            value: `${type[0].toUpperCase() + type.slice(1)} Field`,
            renderer: (item) => {
                if(item.newFieldHeader){
                    const config = newCustomFields[item.newFieldHeader];
                    const enabled = !!config;
                    return (
                        <View style={{gap:6}}>
                            <Checkbox
                                checked={enabled}
                                size={18}
                                label="Import as new custom field"
                                testID={`import-new-custom-field-checkbox-${item.newFieldHeader}`}
                                onChange={() => {
                                    setNewCustomFields((prev) => {
                                        const next = {...prev};
                                        if(next[item.newFieldHeader]) delete next[item.newFieldHeader];
                                        else next[item.newFieldHeader] = {name: item.newFieldHeader, type: "Text"};
                                        return next;
                                    })
                                }}
                            />
                            {enabled && (
                                <View style={{gap:6}}>
                                    <Input
                                        title="Field Name"
                                        placeholder="Field name"
                                        value={config.name}
                                        onUpdate={(new_value) => setNewCustomFields((prev) => ({...prev, [item.newFieldHeader]: {...prev[item.newFieldHeader], name: new_value}}))}
                                    />
                                    <SelectInput
                                        title="Field Type"
                                        placeholder="Field type"
                                        options={NEW_CUSTOM_FIELD_TYPES}
                                        value={config.type}
                                        onUpdate={(new_value) => setNewCustomFields((prev) => ({...prev, [item.newFieldHeader]: {...prev[item.newFieldHeader], type: new_value}}))}
                                    />
                                </View>
                            )}
                        </View>
                    )
                }

                return (
                    <View style={{flexDirection:"row", alignItems:"center", justifyContent:"space-between", gap:5}}>
                        <View style={{flexDirection:"row", alignItems:"center", gap:2}}>
                            <Text style={styles.columnText}>{item.label}{item.required && <Text style={{color:colors.error}}>*</Text>}</Text>
                            {item.type && <Tooltip title={`This field is associated with a ${item.associationName} in Reach. Make sure the values in the column you choose can uniquely identify each record.`}><Unicon name="help-circle-outline" size={16} colorVariant={"textSecondary"}/></Tooltip>}
                        </View>
                        <Tooltip title={mappedColumns[item.name] ? (autoMappedFields[item.name] ? "Auto-matched by column name — verify before importing" : "Mapped") : "Not mapped yet"}>
                            <Unicon name="checkmark-circle" size={22} colorVariant={mappedColumns[item.name] ? (autoMappedFields[item.name] ? "warning" : "success") : "textSecondary"}/>
                        </Tooltip>
                    </View>
                )
            }
        },
        {
            value: "CSV Column",
            renderer: (item) => {
                if(item.newFieldHeader){
                    return <Text style={styles.columnText}>{item.newFieldHeader}</Text>
                }

                const headerKeys = Object.keys(headers);
                
                return (
                    <SelectInput
                        placeholder="Select a column"
                        options={headerKeys}
                        value={mappedColumns[item.name]}
                        onUpdate={(new_value) => {
                            const newMappedColumns = { ...mappedColumns };
                            const existingHeader = Object.keys(newMappedColumns).find(key => newMappedColumns[key] === new_value);

                            if(existingHeader){
                                newMappedColumns[existingHeader] = "";
                            }

                            // A header can't be both a mapped field and a new custom field.
                            if(new_value && newCustomFields[new_value]){
                                setNewCustomFields((prev) => {
                                    const next = {...prev};
                                    delete next[new_value];
                                    return next;
                                })
                            }

                            setMappedColumns({...newMappedColumns, [item.name]: new_value})
                            // User manually changed this mapping — mark as confirmed (not auto-mapped).
                            setAutoMappedFields((prev) => {
                                const next = {...prev};
                                delete next[item.name];
                                // Also clear auto-mapped flag for the field that previously held this header.
                                if(existingHeader) delete next[existingHeader];
                                return next;
                            })
                        }}
                    />
                )
            }
        },
        {
            value: "Sample Values",
            renderer: (item) => {
                const header = item.newFieldHeader || mappedColumns[item.name];
                const [sampleValueWithData, sampleValueWithData2] = sampleValuesFor(header);
                return (
                    <View style={{flexDirection:"row", alignItems:"center", gap:5}}>
                        <Text style={styles.columnText}>{sampleValueWithData?.trim?.() ?? sampleValueWithData}</Text>
                        {sampleValueWithData2 && <Text numberOfLines={1} style={styles.columnText}> •  {sampleValueWithData2?.trim?.() ?? sampleValueWithData2}</Text>}
                    </View>
                )
            }
        }
    ], [type, headers, mappedColumns, csvData, newCustomFields, autoMappedFields]);

    const associationColumns = Object.keys(headers).reduce((acc, header) => {
        const mappedToColumn = Object.keys(mappedColumns).find((key) => mappedColumns[key] === header)
        const associationField = importFields[type].filter((field) => field.type)?.find((field) => field.name === mappedToColumn)
        const addScreenName = associationField?.type === "people" 
            ? "people/create"
            : associationField?.type === "contributionFunds" 
            ? "contributions/funds/create"
            : associationField?.type === "contributionMethods" 
            ? "contributions/methods/create"
            : null  
        
        acc.push({
            value: header,
            width:associationField ? undefined : 180,
            minWidth:associationField ? 280 : 180,
            renderer: (item) => {
                const association = associationField && getImportAssociationForValue(associationField.type, item[header]);
                const selectedValue = association?.associatedItem;
                const isSuggested = association?.suggested === true;

                return (
                    <View>
                        <View>
                            <Tooltip title={item[header]}><Text style={styles.columnText}>{item[header]}</Text></Tooltip>
                        </View>
                        {associationField &&
                            <View style={{flexDirection:"row", alignItems:"center", gap:5}}>
                                <View style={{flex:1}}>
                                    <SheetSelector
                                        titleOnly
                                        placeholder={associationField.associationName}
                                        addScreenName={addScreenName}
                                        value={selectedValue}
                                        type={associationField.type}
                                        onChange={(new_value) => {
                                            const currentIndex = currentAssociations.findIndex((association) => importAssociationKey(association.associatedTable, association.value) === importAssociationKey(associationField.type, item[header]));
                                            const existingAssociation = currentIndex > -1 ? currentAssociations[currentIndex] : null;
                                            const newCurrentAssociation = {
                                                id: existingAssociation?.id,
                                                associatedItem: new_value,
                                                associatedID: new_value?.id,
                                                associatedTable: associationField.type,
                                                value: item[header],
                                                suggested: false
                                            }

                                            if(currentIndex > -1){
                                                const newCurrentAssociations = [...currentAssociations];
                                                newCurrentAssociations[currentIndex] = newCurrentAssociation;
                                                setCurrentAssociations(newCurrentAssociations);
                                            }else{
                                                setCurrentAssociations([...currentAssociations, newCurrentAssociation]);
                                            }
                                        }}
                                    />
                                </View>
                                <View>
                                    <Tooltip title={selectedValue ? (isSuggested ? "Auto-matched by name — click to verify" : "Association linked and will be saved for future imports") : `No association linked for ${associationField.associationName}`}>
                                        <Pressable
                                            disabled={!selectedValue || !isSuggested}
                                            onPress={() => {
                                                const currentIndex = currentAssociations.findIndex((association) => importAssociationKey(association.associatedTable, association.value) === importAssociationKey(associationField.type, item[header]));
                                                if(currentIndex === -1) return;
                                                const newCurrentAssociations = [...currentAssociations];
                                                newCurrentAssociations[currentIndex] = {
                                                    ...newCurrentAssociations[currentIndex],
                                                    suggested: false
                                                };
                                                setCurrentAssociations(newCurrentAssociations);
                                            }}
                                        >
                                            <Unicon name={selectedValue ? "checkmark-circle" : "warning"} size={22} colorVariant={selectedValue ? (isSuggested ? "warning" : "success") : "error"} style={{marginTop:4}}/>
                                        </Pressable>
                                    </Tooltip>
                                </View>
                            </View>
                        }
                    </View>
                )
            }
        })

        return acc;
    }, [])

    const associationHeaders = Object.keys(headers).filter((header) => {
        const mappedToColumn = Object.keys(mappedColumns).find((key) => mappedColumns[key] === header)
        const associationField = importFields[type].filter((field) => field.type)?.find((field) => field.name === mappedToColumn)
        return !!associationField
    });

    const filteredCsvData = useMemo(() => {
        if (associationFilter === 'all') return csvData;

        return csvData.filter((row) => {
            const rowStatuses = associationHeaders.map((header) => {
                const mappedToColumn = Object.keys(mappedColumns).find((key) => mappedColumns[key] === header)
                const associationField = importFields[type].filter((field) => field.type)?.find((field) => field.name === mappedToColumn)
                const association = associationField && getImportAssociationForValue(associationField.type, row[header]);
                if (!association?.associatedItem) return 'unlinked';
                if (association.suggested === true) return 'suggested';
                return 'linked';
            });

            if (associationFilter === 'needsReview') {
                return rowStatuses.some(s => s === 'unlinked' || s === 'suggested');
            }
            if (associationFilter === 'unlinkedOnly') {
                return rowStatuses.some(s => s === 'unlinked');
            }
            return true;
        });
    }, [csvData, associationFilter, associationHeaders, mappedColumns, getImportAssociationForValue, type]);

    const associationColumnsLeftToMap = Object.keys(headers).filter((header) => {
        const mappedToColumn = Object.keys(mappedColumns).find((key) => mappedColumns[key] === header)
        const associationField = importFields[type].filter((field) => field.type)?.find((field) => field.name === mappedToColumn)

        if(!associationField) return false;

        const csvFieldValues = csvData.map((row) => row[header]);
        const associationValues = new Set(
            currentAssociations
                .filter((association) => normalizeImportAssociationTable(association.associatedTable) === normalizeImportAssociationTable(associationField.type))
                .map((association) => normalizeImportAssociationValue(association.value))
        );

        return csvFieldValues.some((value) => {
            const normalizedValue = normalizeImportAssociationValue(value);
            return normalizedValue !== '' && !associationValues.has(normalizedValue);
        })
    });

    // CSV headers not yet mapped to any Reach field (candidates for new custom fields).
    const unmappedHeaders = useMemo(() => {
        const usedHeaders = new Set(Object.values(mappedColumns).filter(Boolean));
        return Object.keys(headers).filter((header) => !usedHeaders.has(header));
    }, [headers, mappedColumns]);

    // Rows shown in the mapping table: standard fields, existing custom fields, and
    // (for people) unmapped CSV columns that can be imported as new custom fields.
    const mappingData = useMemo(() => {
        if(!file) return [];
        const baseFields = [...importFields[type]];
        if(type === "people" && !isFree){
            customFields.forEach((cf) => baseFields.push({name: cf.id, label: cf.name, custom: true}));
            unmappedHeaders.forEach((header) => baseFields.push({newFieldHeader: header}));
        }
        return baseFields;
    }, [file, type, customFields, unmappedHeaders, isFree]);

    // Count CSV rows that match existing people (via ImportAssociations) vs new.
    // Also builds per-row breakdown data for the click-to-expand cards.
    const {peopleImportCounts, importBreakdown} = useMemo(() => {
        if(type !== "people") return {peopleImportCounts: {newCount: 0, existingCount: 0, total: 0}, importBreakdown: {newRows: [], existingRows: []}};
        const idHeader = mappedColumns[personImportFieldGroups.idField];
        const total = csvData.length;

        if(!idHeader || !peopleAssociationValues){
            return {peopleImportCounts: {newCount: total, existingCount: 0, total}, importBreakdown: {newRows: csvData, existingRows: []}};
        }

        const seen = new Set();
        const newRows = [];
        const existingRows = [];
        let existingCount = 0;

        for(const row of csvData){
            const raw = row[idHeader];
            const value = typeof raw === "string" ? raw.trim() : String(raw).trim();
            if(value && peopleAssociationValues.has(value)){
                existingRows.push(row);
                if(!seen.has(value)){
                    seen.add(value);
                    existingCount++;
                }
            } else {
                newRows.push(row);
            }
        }

        return {
            peopleImportCounts: {newCount: total - existingCount, existingCount, total},
            importBreakdown: {newRows, existingRows},
        };
    }, [type, mappedColumns, csvData, peopleAssociationValues]);

    const performPeopleImport = () => {
        // Free orgs never create or map custom fields.
        const fieldsToCreate = isFree ? [] : unmappedHeaders
            .filter((header) => newCustomFields[header])
            .map((header) => ({
                header,
                name: newCustomFields[header].name?.trim() || header,
                type: newCustomFields[header].type || "Text",
            }));

        setConfirmImportOpen(false);
        importDataMutation.mutate({
            data: csvData,
            mappedColumns,
            newCustomFields: fieldsToCreate,
            existingRecordMode,
            customFields: isFree ? [] : customFields,
            additionalParams: params,
        });
    };

    const startPeopleImport = () => {
        const missingRequired = importFields.people.filter((f) => f.required && !mappedColumns[f.name]);
        if(missingRequired.length > 0){
            Toast.show({type: "error", text2: `Please map required fields: ${missingRequired.map((f) => f.label).join(", ")}`});
            return;
        }

        // Default to skip if there are existing records, update otherwise.
        setExistingRecordMode(peopleImportCounts.existingCount > 0 ? 'skip' : 'update');
        setConfirmImportOpen(true);
    };

    return (
        <View style={{flex:1}}>
            {showAssociations
                ? <Table
                    title="Confirm Associations"
                    description={`We've matched your CSV values for associative columns to the appropriate items in Reach. Review the matches and make any necessary adjustments.`}
                    headerRightContent={(
                        <View style={{flexDirection:"row", justifyContent:"flex-end", alignItems:"center", gap:10, flexWrap:"wrap"}}>
                            <SolidButton
                                size="sm"
                                width={100}
                                variant="outlined"
                                text="Cancel"
                                type="textSecondary"
                                onPress={async () => {
                                    setLoadingCSV(false)
                                    setFile(null)
                                    setCsvData([])
                                    setHeaders({})
                                    setMappedColumns({})
                                    setNewCustomFields({})
                                    setAutoMappedFields({})
                                    setShowAssociations(false)
                                }}
                            />
                            <SolidButton
                                size="sm"
                                width={100}
                                iconBefore
                                iconName="cloud-upload-outline"
                                text="Import"
                                disabled={associationColumnsLeftToMap.length > 0}
                                tooltipText={associationColumnsLeftToMap.length > 0 ? `Please resolve all associations before importing` : ""}
                                type="success"
                                onPress={async () => {
                                    // Process data to parse amount values
                                    const amountColumn = mappedColumns.amount;
                                    const processedData = csvData.map(row => ({
                                        ...row,
                                        [amountColumn]: amountColumn && row[amountColumn] 
                                            ? parseFloat(row[amountColumn].replace(/[$,]/g, '')) 
                                            : row[amountColumn]
                                    }));
                                    importDataMutation.mutate({data: processedData, associations: currentAssociations, mappedColumns, additionalParams: params})
                                }}
                            />
                        </View>
                    )}
                    filters={[
                        {label: "Association Status", inputType: "dropdown", value: associationFilter, options: [
                            {label: "All Rows", value: "all"},
                            {label: "Needs Review", value: "needsReview"},
                            {label: "Unlinked Only", value: "unlinkedOnly"},
                        ]},
                    ]}
                    onFiltersChange={(newFilters) => {
                        const statusFilter = newFilters.find(f => f.label === "Association Status");
                        if (statusFilter) setAssociationFilter(statusFilter.value);
                    }}
                    columns={associationColumns}
                    data={filteredCsvData}
                    estimatedItemSize={60}
                    ListEmptyComponent={loadingCSV ? <View style={{marginTop:20}}><ActivityIndicator color={colors.primary} size={28} style={{marginBottom:20}}/></View> : <EmptyListPlaceholder spacing={20} size={100} noLottie message={associationFilter !== 'all' && csvData.length > 0 ? "No rows match the current filter" : "Upload a CSV file to begin mapping fields"} />}
                />
                : <Table
                    key={`import-table-${Object.keys(headers).length}`}
                    testID="import-mapping-list"
                    title="CSV Import Mapping"
                    description={`Here you can upload a CSV file to bulk import ${type} into Reach. Map the CSV columns to the appropriate fields.`}
                    headerRightContent={(
                        <View style={{alignItems:"flex-end", gap:10}}>
                            {!file 
                                ? <SolidButton
                                    size="sm"
                                    width={150}
                                    variant="outlined"
                                    text="Upload CSV File"
                                    iconName="cloud-upload-outline"
                                    onPress={async () => {
                                        const picker = await DocumentPicker.getDocumentAsync({type: ['text/csv']});
                
                                        if(!picker || !picker.assets){
                                            return
                                        }
                
                                        setLoadingCSV(true)
                                        setFile(picker.assets[0])
                                        setHeaders({})
                                        setMappedColumns({})
                                        setNewCustomFields({})
                                        setAutoMappedFields({})
                                    }}
                                />
                                : <View style={{gap:10, alignItems:"flex-end"}}>
                                    <SolidButton
                                        size="sm"
                                        width={150}
                                        text={type === "people" ? "Import" : "Continue"}
                                        type="success"
                                        iconName={type === "people" ? "cloud-upload-outline" : "checkmark-circle"}
                                        onPress={async () => {
                                            if(type === "people"){
                                                startPeopleImport()
                                            } else {
                                                setShowAssociations(true)
                                            }
                                        }}
                                    />
                                    <View style={{flexDirection:"row", gap:10}}>
                                        {importTemplates.length > 0 && (
                                            <DropdownMenu
                                                forceToolbar
                                                title="Select a Template"
                                                triggerComponent={<SolidButton
                                                    size="sm"
                                                    width={150}
                                                    text="Map From Template"
                                                    variant="outlined"
                                                    type="textSecondary"
                                                />}
                                                options={importTemplates.filter(t => JSON.stringify(t.mapping) != JSON.stringify(mappedColumns)).map(t => ({name:t.name, onSelect: async () => {
                                                    const filteredMappings = Object.fromEntries(Object.entries(t.mapping).filter(([,header]) => Object.keys(headers).includes(header)))
                                                    setMappedColumns(filteredMappings)
                                                }}))}
                                            />
                                        )}

                                        {Object.keys(mappedColumns).length > 0 && (
                                            <SolidButton
                                                size="sm"
                                                width={150}
                                                text="Create Template"
                                                variant="outlined"
                                                type="textSecondary"
                                                onPress={async () => {
                                                    setCreateTemplateModalOpen(true)
                                                }}
                                            />
                                        )}
                                    </View>
                                </View>
                            }
                            {file && <View style={{flexDirection:"row", alignItems:"center", gap:5}}>
                                <Text style={{color:colors.textSecondary, marginBottom:5, fontSize:12}}>{file.name} ({formatSize(file.size)})</Text>
                                <Pressable style={{marginTop:-2}} onPress={() => {
                                    setFile(null)
                                    setCsvData([])
                                    setHeaders({})
                                    setMappedColumns({})
                                    setNewCustomFields({})
                                    setAutoMappedFields({})
                                }}><Unicon name="close" size={18} colorVariant={"textSecondary"} /></Pressable>
                            </View>}
                        </View>
                    )}
                    columns={mappingColumns}
                    data={mappingData}
                    estimatedItemSize={60}
                    ListEmptyComponent={loadingCSV ? <View style={{marginTop:20}}><ActivityIndicator color={colors.primary} size={28} style={{marginBottom:20}}/></View> : <EmptyListPlaceholder spacing={20} size={100} noLottie message="Upload a CSV file to begin mapping fields" />}
                />
            }
            <SheetModal 
                title="Create Template" 
                helpText="Create a template from the current mapping to use for future imports" 
                isOpen={createTemplateModalOpen} 
                onCancel={() => setCreateTemplateModalOpen(false)}
                confirmText="Create"
                onConfirm={() => {
                    if(!newTemplateName || newTemplateName.trim() === ""){
                        Toast.show({type: 'error',text2: "Please enter a name for this template."})
                        return
                    }

                    createTemplateMutation.mutate({name: newTemplateName, mapping: mappedColumns, type: type})
                }}
            >
                <View style={{padding:10}}>
                    <Input
                        placeholder="Template Name"
                        value={newTemplateName}
                        onUpdate={(e) => setNewTemplateName(e)}
                    />
                </View>
            </SheetModal>
            <SheetModal
                title="Confirm Import"
                helpText="Review the summary below before importing."
                isOpen={confirmImportOpen}
                cancelText="Cancel"
                confirmText="Import"
                loading={importDataMutation.isPending}
                onCancel={() => setConfirmImportOpen(false)}
                onConfirm={performPeopleImport}
            >
                <View style={{padding:10, gap:16}}>
                    <View style={{flexDirection:"row", gap:20}}>
                        <Pressable style={{flex:1, alignItems:"center", padding:12, borderRadius:8, backgroundColor:colors.success + "15"}} onPress={() => setBreakdownView('new')}>
                            <Text style={{fontSize:24, fontFamily:fonts.heading, color:colors.success}}>{peopleImportCounts.newCount}</Text>
                            <Text style={{color:colors.textSecondary, fontSize:12, marginTop:4}}>New People</Text>
                        </Pressable>
                        {peopleImportCounts.existingCount > 0 && (
                            <Pressable style={{flex:1, alignItems:"center", padding:12, borderRadius:8, backgroundColor:colors.warning + "15"}} onPress={() => setBreakdownView('existing')}>
                                <Text style={{fontSize:24, fontFamily:fonts.heading, color:colors.warning}}>{peopleImportCounts.existingCount}</Text>
                                <Text style={{color:colors.textSecondary, fontSize:12, marginTop:4}}>Existing Records</Text>
                            </Pressable>
                        )}
                        <View style={{flex:1, alignItems:"center", padding:12, borderRadius:8, backgroundColor:colors.primary + "15"}}>
                            <Text style={{fontSize:24, fontFamily:fonts.heading, color:colors.primary}}>{peopleImportCounts.total}</Text>
                            <Text style={{color:colors.textSecondary, fontSize:12, marginTop:4}}>Total Rows</Text>
                        </View>
                    </View>

                    {peopleImportCounts.existingCount > 0 && (
                        <View style={{gap:8}}>
                            <Text style={{color:colors.text, fontSize:14, fontFamily:fonts.body}}>{peopleImportCounts.existingCount} record{peopleImportCounts.existingCount === 1 ? "" : "s"} already exist in Reach. What should we do with them?</Text>
                            <Pressable
                                style={{flexDirection:"row", alignItems:"center", gap:8, padding:8}}
                                onPress={() => setExistingRecordMode("skip")}
                            >
                                <Checkbox checked={existingRecordMode === "skip"} size={20} onChange={() => setExistingRecordMode("skip")}/>
                                <View style={{flex:1}}>
                                    <Text style={{color:colors.text, fontSize:14}}>Skip existing records</Text>
                                    <Text style={{color:colors.textSecondary, fontSize:12}}>Only create new people. Leave existing ones unchanged.</Text>
                                </View>
                            </Pressable>
                            <Pressable
                                style={{flexDirection:"row", alignItems:"center", gap:8, padding:8}}
                                onPress={() => setExistingRecordMode("update")}
                            >
                                <Checkbox checked={existingRecordMode === "update"} size={20} onChange={() => setExistingRecordMode("update")}/>
                                <View style={{flex:1}}>
                                    <Text style={{color:colors.text, fontSize:14}}>Update existing records</Text>
                                    <Text style={{color:colors.textSecondary, fontSize:12}}>Merge imported field values into existing people.</Text>
                                </View>
                            </Pressable>
                        </View>
                    )}

                    {Object.keys(newCustomFields).length > 0 && (
                        <View style={{padding:10, borderRadius:8, backgroundColor:colors.primary + "10"}}>
                            <Text style={{color:colors.textSecondary, fontSize:12}}>
                                {Object.keys(newCustomFields).length} new custom field{Object.keys(newCustomFields).length === 1 ? "" : "s"} will be created.
                            </Text>
                        </View>
                    )}
                </View>
            </SheetModal>
            <SheetModal
                title={breakdownView === 'new' ? 'New People' : 'Existing Records'}
                helpText={breakdownView === 'new' ? 'These rows will be created as new people.' : 'These rows match existing people via Source ID.'}
                isOpen={!!breakdownView}
                cancelText="Close"
                onCancel={() => setBreakdownView(null)}
            >
                <View style={{padding:10, maxHeight: 500}}>
                    <Table
                        columns={(() => {
                            const idHeader = mappedColumns[personImportFieldGroups.idField];
                            const firstNameHeader = mappedColumns.firstName;
                            const lastNameHeader = mappedColumns.lastName;
                            const emailHeader = mappedColumns.email;
                            return [
                                {
                                    value: "Source ID",
                                    renderer: (item) => <Text style={styles.columnText}>{idHeader ? item[idHeader] : ''}</Text>,
                                    width: 120,
                                },
                                {
                                    value: "First Name",
                                    renderer: (item) => <Text style={styles.columnText}>{firstNameHeader ? item[firstNameHeader] : ''}</Text>,
                                },
                                {
                                    value: "Last Name",
                                    renderer: (item) => <Text style={styles.columnText}>{lastNameHeader ? item[lastNameHeader] : ''}</Text>,
                                },
                                {
                                    value: "Email",
                                    renderer: (item) => <Text style={styles.columnText}>{emailHeader ? item[emailHeader] : ''}</Text>,
                                },
                            ];
                        })()}
                        data={breakdownView === 'new' ? importBreakdown.newRows : importBreakdown.existingRows}
                        estimatedItemSize={50}
                        idAccessor={personImportFieldGroups.idField}
                        ListEmptyComponent={<EmptyListPlaceholder spacing={20} size={80} noLottie message="No rows" />}
                    />
                </View>
            </SheetModal>
        </View>
    );
}


const styles = StyleSheet.create(({colors, fonts}) => ({
    columnText: {
        fontFamily:fonts.body,
        color:colors.textSecondary
    }
}))